import moment from 'moment';

/**
 * The period the whole screen is scoped to, and the period it is compared with.
 *
 * One picker at the top; every panel reads the same window. The comparison is
 * chosen to be a fair one rather than simply "the days before": today is
 * measured against the same weekday last week, because a Tuesday is not a
 * Saturday and a restaurant week is not flat.
 */

export const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'custom', label: 'Custom' },
];

const DAY = 'YYYY-MM-DD';

const range = (start, end) => ({
  start: moment(start).format(DAY),
  end: moment(end).format(DAY),
});

/**
 * A period, resolved to two dates and the window it is judged against.
 *
 * `days` is inclusive of both ends, so Today is 1 and not 0 — several panels
 * only appear once it is more than one.
 */
export function resolvePeriod(id, custom = {}) {
  const today = moment().startOf('day');

  if (id === 'yesterday') {
    const day = moment(today).subtract(1, 'day');
    return finish('Yesterday', range(day, day), range(moment(day).subtract(7, 'days'), moment(day).subtract(7, 'days')), 'the same day last week');
  }

  if (id === 'week') {
    const start = moment(today).startOf('isoWeek');
    return finish('This week', range(start, today), range(moment(start).subtract(7, 'days'), moment(today).subtract(7, 'days')), 'the same days last week');
  }

  if (id === 'month') {
    const start = moment(today).startOf('month');
    const previousStart = moment(start).subtract(1, 'month');
    // The same number of days into last month, so a month-to-date figure is
    // never compared with a whole month.
    const previousEnd = moment(previousStart).add(today.diff(start, 'days'), 'days');
    return finish('This month', range(start, today), range(previousStart, previousEnd), 'the same days last month');
  }

  if (id === 'custom' && custom.start && custom.end) {
    const start = moment(custom.start).startOf('day');
    const end = moment(custom.end).startOf('day');
    const span = Math.max(0, end.diff(start, 'days'));
    const previousEnd = moment(start).subtract(1, 'day');
    const previousStart = moment(previousEnd).subtract(span, 'days');
    return finish('Custom', range(start, end), range(previousStart, previousEnd), 'the period before it');
  }

  return finish('Today', range(today, today), range(moment(today).subtract(7, 'days'), moment(today).subtract(7, 'days')), 'the same day last week');
}

function finish(label, current, previous, comparisonLabel) {
  const days = moment(current.end).diff(moment(current.start), 'days') + 1;
  return {
    label,
    start: current.start,
    end: current.end,
    days,
    isSingleDay: days <= 1,
    previous: { ...previous, label: comparisonLabel },
  };
}

/**
 * The window the day-by-day chart draws.
 *
 * A single day has no shape of its own, so Today and Yesterday show the
 * fortnight they sit at the end of — the day means nothing without the days
 * around it. A longer period is its own chart.
 */
export function trendWindow(period) {
  if (!period.isSingleDay) {
    return { start: period.start, end: period.end, label: `${period.days} days` };
  }
  return {
    start: moment(period.end).subtract(13, 'days').format(DAY),
    end: period.end,
    label: 'last 14 days',
  };
}

/**
 * One fetch covers the period and the chart behind it.
 *
 * A day earlier than either needs, because the server filters on the order's
 * own `date` field — which the till writes shifted — while everything here
 * buckets by `created_at`. The extra day stops an evening order falling off
 * the edge of the chart.
 */
export function fetchWindow(period) {
  const trend = trendWindow(period);
  const start = moment.min(moment(period.start), moment(trend.start)).subtract(1, 'day');
  return { start: start.format(DAY), end: period.end };
}

/** "Tuesday, 8 September", or "1 – 8 September" for a range. */
export function describePeriod(period) {
  const start = moment(period.start);
  const end = moment(period.end);
  if (period.isSingleDay) return start.format('dddd, D MMMM');
  if (start.isSame(end, 'month')) return `${start.format('D')} – ${end.format('D MMMM')}`;
  return `${start.format('D MMM')} – ${end.format('D MMM')}`;
}
